import {DescriptionRounded, ExpandMore} from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    Grid,
    IconButton,
    TextField,
    Typography
} from "@mui/material";
import * as React from "react";
import {useMemo, useRef, useState} from "react";
import {useFormContext} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import AgGridCustomTable from "../../../../../../util/component/AgGridCustomTable";
import {ColDef, GetRowIdParams, GridOptions, ICellRendererParams, IServerSideGetRowsParams} from "ag-grid-enterprise";
import dayjs from "dayjs";
import {useAgGridLocale} from "../../../../../../config/globalConfig";
import {AgGridReact} from "ag-grid-react";
import {useRecoilValue} from "recoil";
import {EDITING} from "../../../../service/FinesSecurityRecoil";
import {useTheftMisappropriationActions} from "../../../../service/useTheftMisappropriationActions";
import {useEventTypeValues} from "../../../../component/useEventTypeValues";
import {useStatusValues} from "../../../../component/useStatusValues";
import EmptyState from "../../../../../../util/component/EmptyState";

const Other = (props: any) => {
    const {t} = useTranslation(['finessecurity'], {keyPrefix: 'theftMisappropriate'});
    const {id} = useParams();
    const [isExpanded, setIsExpanded] = useState(true);
    const agGridLocale = useAgGridLocale();
    const gridRef = useRef<AgGridReact>(null);
    const {register} = useFormContext();
    const editing = useRecoilValue(EDITING);
    const {getTheftMisappropriationOther} = useTheftMisappropriationActions();
    const theftMisappropriate = props.theftMisappropriate;
    const {getEventTypeValues} = useEventTypeValues();
    const {getStatusValues, getStatusColor} = useStatusValues();

    const columns: ColDef[] = [
        {
            field: "id",
            headerName: t('other.id'),
            unSortIcon: true,
            valueGetter: (params: any) => (params.data.id),
            tooltipValueGetter: (params: any) => (params.data.id),
        },
        {
            field: "openingDatetime",
            headerName: t('listColumns.openingDate'),
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "",
            unSortIcon: true,
        },
        {
            field: "closingDatetime",
            headerName: t('listColumns.closingDate'),
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "",
            unSortIcon: true,
        },
        {
            field: "eventType",
            headerName: t('filters.eventType'),
            sortable: false,
            sort: undefined,
            cellRenderer: (params: any) => getEventTypeValues(params.value.eventTypeCode),
            tooltipValueGetter: (params: any) => getEventTypeValues(params.value.eventTypeCode),
        },
        {
            field: "eventStatus",
            headerName: t('filters.status'),
            cellRenderer: (params: any) => {
                return <Chip label={getStatusValues(params.value.eventStatusCode)}
                             sx={{backgroundColor: getStatusColor(params.value.eventStatusCode)}}/>
            }
        },
        {
            field: "_actions",
            headerName: t('eventCard.action'),
            pinned: 'right',
            lockPosition: 'right',
            lockPinned: true,
            sortable: false,
            cellRenderer: (params: ICellRendererParams) => {
                return (
                    <Box display="flex" justifyContent="center" alignItems="center">
                        <IconButton color={"accent"}
                                    onClick={() => {
                                        window.open(`/finessecurity/theft-misappropriation/detail/${params.data.id}`)
                                    }}>
                            <DescriptionRounded/>
                        </IconButton>
                    </Box>
                )
            }
        }
    ];

    const toolbar = {
        renderInfo: () =>
            <Typography align="left">{t('other.otherList').toUpperCase()}</Typography>
    }

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        suppressRowClickSelection: true,
        domLayout: 'autoHeight',
        rowModelType: 'serverSide',
        pagination: false,
        cacheBlockSize: 10,
        defaultColDef: {
            sortable: true,
            resizable: false,
            wrapHeaderText: true,
            autoHeight: true,
        },
        columnDefs: columns,
        serverSideInfiniteScroll: true,
        localeText: agGridLocale,
        onGridReady: (params) => {
            params.api.setServerSideDatasource(createDatasource());
        },
        serverSideDatasource: createDatasource()
    };

    function createDatasource() {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                try {

                    if (theftMisappropriate.idFirstDriver || theftMisappropriate.idAdditionalDriver) {
                        let driversIds = [theftMisappropriate.idFirstDriver, theftMisappropriate.idAdditionalDriver];

                        let data = await getTheftMisappropriationOther(id, driversIds, theftMisappropriate.tenant, theftMisappropriate.dealer);

                        if (data.totalElements === 0) {
                            gridRef.current!.api.showNoRowsOverlay();
                        } else {
                            gridRef.current!.api.hideOverlay();
                        }
                        params.success({
                            rowData: data?.content,
                            rowCount: data?.totalElements
                        });
                        if (data.totalElements === 0) {
                            gridRef.current!.api.showNoRowsOverlay();
                        } else {
                            gridRef.current!.api.hideOverlay();
                        }
                    } else {
                        gridRef.current!.api.showNoRowsOverlay();
                        params.success({
                            rowData: [],
                            rowCount: 0
                        });
                    }
                } catch (e) {
                    params.fail();
                }
            }
        };
    }

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);
    const noRowsOverlayComponentParams = useMemo<any>(() => {
        return {
            title: t("noEvents")
        };
    }, []);

    return (
        <Accordion defaultExpanded={true} onChange={(_e, expanded) => {
            setIsExpanded(expanded);
        }}>
            <AccordionSummary expandIcon={<ExpandMore/>}>
                <Grid item md={6} xs={6}>
                    {t("tabs.other")}
                </Grid>
            </AccordionSummary>
            <AccordionDetails>

                <Grid container>
                    <Grid item xs={12} sx={{padding: "16px"}}>
                        {theftMisappropriate &&
                            <AgGridCustomTable gridOptions={gridOptions} gridRef={gridRef} toolbar={toolbar}
                                               noRowsOverlayComponent={noRowsOverlayComponent}
                                               noRowsOverlayComponentParams={noRowsOverlayComponentParams}/>
                        }
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        id="notes"
                        disabled={!editing}
                        label={t("eventCard.note")}
                        type="text"
                        {...register("notes")}
                    />
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}

export default Other;